<template>
  <div
    :id="id"
    class="note-editor"
    :class="{ standalone: standalone, fullwidth: fullwidth }"
    @keydown="handleGlobalKeydown"
  >
    <div class="editor-wrapper">
      <NoteSearchBar
        v-model="searchQuery"
        :visible="searchVisible"
        :current-match="currentMatchIndex + 1"
        :total-matches="searchMatches.length"
        @next="goToNextMatch"
        @previous="goToPreviousMatch"
        @close="closeSearch"
      />
      <div class="textarea-container" :class="{ 'with-search': searchVisible }">
        <div
          ref="highlightOverlay"
          class="highlight-overlay"
          v-html="highlightedText"
        />
        <textarea
          ref="textarea"
          v-model="fullNote"
          class="note-textarea"
          @change="onContentChange()"
          @keyup="onContentChange()"
          @scroll="syncOverlayScroll"
          @input="updateSearchMatches"
        />
      </div>
    </div>
    <div class="note-toolbar">
      <div class="fixed bottom-left">
        <div v-if="saving" class="note-spinner">
          <i class="fa fa-spin fa-spinner" />
        </div>
        <button
          v-if="!searchVisible"
          id="btn-search"
          class="btn-big-circle btn-search-mobile"
          :title="$t.get('Search in note')"
          :aria-label="$t.get('Search in note')"
          @click="openSearch"
        >
          <i class="fa fa-search" aria-hidden="true" />
        </button>
      </div>
      <div class="editor-menu fixed bottom-right">
        <span style="display: inline-block">
          <div v-if="menuOpen" class="upmenu">
            <button
              id="btn-del-note"
              class="btn-big-circle btn-danger"
              @click="deleteNote()"
            >
              <i class="fa fa-trash" />
            </button>
          </div>
          <button
            class="btn-big-circle btn-secondary"
            @click="menuOpen = !menuOpen"
          >
            <i class="fa fa-cog" />
          </button>
        </span>
        <span style="display: inline-block">
          <button
            id="btn-save"
            :class="{ disabled: !contentDirty }"
            class="btn-big-circle"
            @click="saveNote()"
          >
            <i class="fa fa-save" />
          </button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { setWindowTitle } from '@/services/WindowService';
import NoteSearchBar from './NoteSearchBar.vue';

export default {
  components: {
    NoteSearchBar,
  },
  props: {
    id: {
      type: String,
      default: 'note-editor',
    },
    fullwidth: {
      type: Boolean,
      default: false,
    },
    embedded: {
      type: Boolean,
      default: false,
    }
  },
  emits: ['delete'],
  data: function () {
    return {
      fullNote: '',
      menuOpen: false,
      saving: false,
      contentDirty: false,
      note: null,
      contentCheckInterval: 2000,
      standalone: false,
      searchVisible: false,
      searchQuery: '',
      searchMatches: [],
      currentMatchIndex: -1,
      resizeHandler: null,
      resizeObserver: null,
      editorResizeObserver: null,
      overlayDimensions: { width: 0, height: 0 },
    };
  },
  watch: {
    id: function (newId) {
      this.getNote(newId);
    },
    searchQuery: function () {
      this.updateSearchMatches();
    },
    fullNote: function () {
      if (this.searchQuery) {
        this.updateSearchMatches();
      }
      this.$nextTick(() => {
        this.syncOverlayStyles();
      });
    },
    searchVisible: function () {
      this.$nextTick(() => {
        this.syncOverlayStyles();
      });
    },
    '$el': {
      handler() {
        this.$nextTick(() => {
          if (this.searchVisible && this.searchQuery) {
            this.syncOverlayStyles();
          }
        });
      },
      immediate: false,
    },
  },
  mounted: function () {
    if (this.$route.name === 'Note Editor') {
      if (window.isElectron) {
        this.getNote(this.id);
        this.standalone = this.$route.query?.standalone === 'true';
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        this.standalone = urlParams.get('popup') === 'true';
        this.getNote(this.$route.params.id);
      }
    } else {
      this.getNote(this.id);
    }
    this.$refs.textarea.focus();
    // Set the textarea cursor to the start of the text
    this.$refs.textarea.setSelectionRange(0, 0);

    this.checkContentTimer = setInterval(() => {
      this.checkContent();
    }, this.contentCheckInterval);

    this.$nextTick(() => {
      if (this.$refs.textarea && this.$refs.highlightOverlay) {
        this.syncOverlayStyles();
        let resizeTimeout;
        const handleResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            this.syncOverlayStyles();
          }, 50);
        };
        window.addEventListener('resize', handleResize);
        this.resizeHandler = handleResize;
        
        if (window.ResizeObserver && this.$refs.textarea) {
          this.resizeObserver = new ResizeObserver(() => {
            this.syncOverlayStyles();
          });
          this.resizeObserver.observe(this.$refs.textarea);
        }
        
        if (window.ResizeObserver && this.$el) {
          this.editorResizeObserver = new ResizeObserver(() => {
            this.syncOverlayStyles();
          });
          this.editorResizeObserver.observe(this.$el);
        }
      }
    });
  },
  beforeUnmount() {
    if (this.checkContentTimer) {
      clearInterval(this.checkContentTimer);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.editorResizeObserver) {
      this.editorResizeObserver.disconnect();
    }
  },
  methods: {
    setupNote: function () {
      this.fullNote = this.note.title + '\n' + this.note.text;
      if (!this.embedded) {
        setWindowTitle(this.note.title);
      }
      this.$nextTick(() => {
        this.syncOverlayStyles();
        if (this.searchQuery) {
          this.updateSearchMatches();
        }
      });
    },
    checkContent: function () {
      if (this.contentDirty) {
        this.saveNote();
      }
    },
    onContentChange: function () {
      const parts = this.fullNote.split('\n');
      this.note.title = parts.length > 0 ? parts[0] : 'New Note';
      this.note.text = parts.length > 1 ? parts.slice(1).join('\n') : '';
      this.contentDirty = true;

      if (!this.embedded) {
        setWindowTitle(this.note.title);
      }
    },
    getNote: function (id) {
      this.$api
        .get('/notes/' + id)
        .then((res) => {
          this.note = res.data;
          this.setupNote();
        })
        .catch((err) => {
          if (
            err.response &&
            err.response.status === 404 &&
            !this.embedded && !this.standalone
          ) {
            this.$router.push({ name: 'Notes' });
            return;
          }
          // TODO: Again .. no error message.  We should show something to the user to indicate that the note could not be loaded and they should try again.
          console.error(err);
        });
    },
    saveNote: function () {
      this.saving = true;
      this.$api
        .put('/notes/' + this.note.id, this.note)
        .then((res) => {
          this.contentDirty = false;
          this.saving = false;
          this.emitter.emit('notes-page:note-updated', res.data);

          // If in popup window, notify main window to update notes
          if (this.standalone) {
            if (window.isElectron) {
              window.electronAPI.notifyMainWindow(
                'notes-page:note-updated',
                res.data
              );
            } else {
              localStorage.setItem('notes_updated_ts', new Date().getTime());
            }
          }

        })
        .catch((err) => {
          // TODO: Show an actual error here ... something with toasted if possible.
          console.error(err);
          // this.saving = false;
        });
    },
    deleteNote: function () {
      if (!window.confirm('Are you SURE you want to delete this note?')) {
        return;
      }

      this.$api
        .delete('/notes/' + this.note.id)
        .then((res) => {
          // Notify about the deletion
          this.emitter.emit('notes-page:note-deleted', this.note.id);

          // Handle popup/standalone window differently
          if (this.standalone) {
            if (window.isElectron) {
              window.electronAPI.notifyMainWindow('notes-page:note-deleted', this.note.id);
              window.electronAPI.closePopup();
            } else {
              localStorage.setItem('notes_updated_ts', new Date().getTime());
              window.close();
            }
          } else {
            this.$toasted.success(this.$t.get('Note deleted'));

            // In the small main window, navigate to notes list
            if (!this.embedded) {
              this.$router.push({ name: 'Notes' });
            }
          }
        })
        .catch((err) => {
          console.error('[Editor] Error deleting note:', err);
        });
      this.$emit('delete', this.note.id);
    },
    handleGlobalKeydown(event) {
      if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
        event.preventDefault();
        this.openSearch();
      }
      if (event.key === 'Escape' && this.searchVisible) {
        event.preventDefault();
        this.closeSearch();
      }
    },
    openSearch() {
      this.searchVisible = true;
      this.$nextTick(() => {
        if (this.searchQuery) {
          this.updateSearchMatches();
        }
      });
    },
    closeSearch() {
      this.searchVisible = false;
      this.searchQuery = '';
      this.searchMatches = [];
      this.currentMatchIndex = -1;
      this.$refs.textarea?.focus();
    },
    updateSearchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        this.searchMatches = [];
        this.currentMatchIndex = -1;
        return;
      }

      const text = this.fullNote;
      const query = this.searchQuery.trim();
      const regex = new RegExp(`\\b${this.escapeRegex(query)}\\b`, 'gi');
      const matches = [];
      let match;

      regex.lastIndex = 0;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
        });
      }

      this.searchMatches = matches;

      if (matches.length > 0 && (this.currentMatchIndex < 0 || this.currentMatchIndex >= matches.length)) {
        this.currentMatchIndex = 0;
        this.scrollToMatch(0);
      } else if (matches.length === 0) {
        this.currentMatchIndex = -1;
      }
      
      this.$nextTick(() => {
        this.syncOverlayStyles();
      });
    },
    escapeRegex(str) {
      return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    },
    goToNextMatch() {
      if (this.searchMatches.length === 0) return;
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
      this.scrollToMatch(this.currentMatchIndex);
    },
    goToPreviousMatch() {
      if (this.searchMatches.length === 0) return;
      this.currentMatchIndex = this.currentMatchIndex <= 0 
        ? this.searchMatches.length - 1 
        : this.currentMatchIndex - 1;
      this.scrollToMatch(this.currentMatchIndex);
    },
    scrollToMatch(index) {
      if (index < 0 || index >= this.searchMatches.length) return;

      const match = this.searchMatches[index];
      const textarea = this.$refs.textarea;
      if (!textarea) return;

      textarea.setSelectionRange(match.start, match.end);
      
      this.$nextTick(() => {
        const textareaRect = textarea.getBoundingClientRect();
        const textareaStyle = window.getComputedStyle(textarea);
        const lineHeight = parseInt(textareaStyle.lineHeight) || 20;
        const paddingTop = parseInt(textareaStyle.paddingTop) || 8;

        const textBeforeMatch = this.fullNote.substring(0, match.start);
        const lines = textBeforeMatch.split('\n');
        const lineNumber = lines.length - 1;

        const scrollTop = lineNumber * lineHeight - textareaRect.height / 2 + paddingTop;
        textarea.scrollTop = Math.max(0, scrollTop);
      });
    },
    syncOverlayScroll() {
      if (this.$refs.highlightOverlay && this.$refs.textarea) {
        const overlay = this.$refs.highlightOverlay;
        const textarea = this.$refs.textarea;
        
        overlay.scrollTop = textarea.scrollTop;
        overlay.scrollLeft = textarea.scrollLeft;
        
        overlay.style.overflow = 'hidden';
      }
    },
    syncOverlayStyles() {
      if (!this.$refs.highlightOverlay || !this.$refs.textarea) return;

      const textarea = this.$refs.textarea;
      const overlay = this.$refs.highlightOverlay;
      const style = window.getComputedStyle(textarea);

      overlay.style.fontSize = style.fontSize;
      overlay.style.fontFamily = style.fontFamily;
      overlay.style.fontWeight = style.fontWeight;
      overlay.style.fontStyle = style.fontStyle;
      overlay.style.fontVariant = style.fontVariant;
      overlay.style.lineHeight = style.lineHeight;
      overlay.style.letterSpacing = style.letterSpacing;
      overlay.style.wordSpacing = style.wordSpacing;
      overlay.style.textIndent = style.textIndent;
      overlay.style.textTransform = style.textTransform;
      overlay.style.padding = style.padding;
      overlay.style.paddingTop = style.paddingTop;
      overlay.style.paddingRight = style.paddingRight;
      overlay.style.paddingBottom = style.paddingBottom;
      overlay.style.paddingLeft = style.paddingLeft;
      overlay.style.border = style.border;
      overlay.style.borderWidth = style.borderWidth;
      overlay.style.boxSizing = 'border-box';
      overlay.style.whiteSpace = style.whiteSpace;
      overlay.style.wordWrap = style.wordWrap;
      overlay.style.wordBreak = style.wordBreak;
      overlay.style.overflowWrap = style.overflowWrap;
      overlay.style.textAlign = style.textAlign;
      overlay.style.direction = style.direction;
      overlay.style.fontKerning = style.fontKerning;
      overlay.style.textRendering = style.textRendering;
      overlay.style.webkitFontSmoothing = style.webkitFontSmoothing;
      overlay.style.mozOsxFontSmoothing = style.mozOsxFontSmoothing;
      
      overlay.style.fontVariantLigatures = style.fontVariantLigatures;
      overlay.style.fontVariantNumeric = style.fontVariantNumeric;
      overlay.style.fontVariantCaps = style.fontVariantCaps;
      overlay.style.fontStretch = style.fontStretch;
      overlay.style.textDecoration = style.textDecoration;
      overlay.style.textDecorationColor = style.textDecorationColor;
      overlay.style.textDecorationStyle = style.textDecorationStyle;
      overlay.style.textDecorationThickness = style.textDecorationThickness;
      overlay.style.textUnderlineOffset = style.textUnderlineOffset;
      overlay.style.textShadow = style.textShadow;
      
      const computedWidth = textarea.clientWidth;
      const computedHeight = textarea.clientHeight;
      
      if (this.overlayDimensions.width !== computedWidth || this.overlayDimensions.height !== computedHeight) {
        this.overlayDimensions.width = computedWidth;
        this.overlayDimensions.height = computedHeight;
        
        this.$nextTick(() => {
          if (this.searchQuery && this.searchMatches.length > 0) {
            this.$forceUpdate();
          }
        });
      }
      
      overlay.style.width = computedWidth + 'px';
      overlay.style.height = computedHeight + 'px';
      overlay.style.margin = '0';
      overlay.style.borderWidth = '0';
      overlay.style.borderStyle = 'none';
      
      overlay.style.position = 'absolute';
      overlay.style.top = '0';
      overlay.style.left = '0';
      
      overlay.style.marginLeft = '0';
      overlay.style.marginTop = '0';
      overlay.style.marginRight = '0';
      overlay.style.marginBottom = '0';
      overlay.style.right = 'auto';
      overlay.style.bottom = 'auto';
      
      overlay.style.overflow = 'hidden';
      overlay.style.overflowX = 'hidden';
      overlay.style.overflowY = 'hidden';
      
      overlay.style.outline = 'none';
      overlay.style.outlineWidth = '0';
      
      this.$nextTick(() => {
        this.syncOverlayScroll();
      });
    },
    escapeHtml(text) {
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },
  },
  computed: {
    highlightedText() {
      void this.overlayDimensions.width;
      void this.overlayDimensions.height;
      
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.escapeHtml(this.fullNote);
      }

      const query = this.searchQuery.trim();
      const escapedQuery = this.escapeRegex(query);
      const regex = new RegExp(`\\b${escapedQuery}\\b`, 'gi');
      let text = this.fullNote;
      let result = '';
      let matchIndex = 0;
      let lastIndex = 0;
      let match;

      regex.lastIndex = 0;

      while ((match = regex.exec(text)) !== null) {
        result += this.escapeHtml(text.substring(lastIndex, match.index));
        
        const isCurrent = matchIndex === this.currentMatchIndex;
        const highlightClass = isCurrent ? 'highlight-current' : 'highlight-match';
        
        const matchText = match[0];
        result += `<span class="${highlightClass}">${this.escapeHtml(matchText)}</span>`;
        
        lastIndex = match.index + match[0].length;
        matchIndex++;
      }

      result += this.escapeHtml(text.substring(lastIndex));

      return result;
    },
  },
};
</script>

<style lang="scss" scoped>
.note-editor {
  top: $topbar-height;
  left: 0px;
  right: 0px;
  bottom: 0px;
  position: absolute;
  overflow-y: hidden;
  display: flex;
  flex-direction: column;
}

.note-editor.standalone {
  top: 0px;
  height: 100vh;
  bottom: 0;
}

.editor-wrapper {
  flex: 1;
  position: relative;
  overflow: hidden;
  min-height: 0;
  
  @media screen and (max-width: $breakpoint-md) {
    min-height: 0;
    max-height: 100%;
  }
}

.textarea-container {
  position: relative;
  width: 100%;
  height: 100%;
  flex: 1;
  overflow: hidden;
  
  &.with-search {
    height: calc(100% - 53px);
    
    @media screen and (min-width: $breakpoint-md) {
      height: calc(100% - 50px);
    }
  }
  
  @media screen and (max-width: $breakpoint-md) {
    min-height: 0;
    max-height: 100%;
  }
}

.highlight-overlay {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden !important;
  color: transparent;
  z-index: 1;
  border: 0px;
  margin: 0 !important;
  box-sizing: border-box !important;
  
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  tab-size: 4;
  -moz-tab-size: 4;
  
  font-kerning: none;
  text-rendering: geometricPrecision;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  
  font-variant-ligatures: none;
  font-variant-numeric: normal;
  font-variant-caps: normal;
  font-stretch: normal;
  text-decoration: none;
  text-shadow: none;
  outline: none;
  
  @media screen and (max-width: $breakpoint-md) {
    transform: translate(0, 0) !important;
    will-change: scroll-position;
    right: auto;
    bottom: auto;
    overflow: hidden !important;
  }
  
  :deep(.highlight-match) {
    background-color: rgba(255, 255, 0, 0.3);
    color: transparent;
  }
  
  :deep(.highlight-current) {
    background-color: rgba(255, 200, 0, 0.6);
    color: transparent;
    font-weight: bold;
  }
}

.note-textarea {
  position: relative;
  z-index: 2;
  resize: none;
  width: 100%;
  height: 100%;
  border: 0px;
  padding: 8px;
  background: transparent;
  color: #2c3e50;
  box-sizing: border-box;
  margin: 0;
  
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: break-word;
  tab-size: 4;
  -moz-tab-size: 4;
  
  font-kerning: none;
  text-rendering: geometricPrecision;
  
  font-variant-ligatures: none;
  font-variant-numeric: normal;
  font-variant-caps: normal;
  font-stretch: normal;
  text-decoration: none;
  text-shadow: none;
  outline: none;
  
  @media screen and (max-width: $breakpoint-md) {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    border-radius: 0;
    min-height: 0;
    max-height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
}

.note-textarea:first-line,
.note-textarea::first-line {
  color: $col-button;
  font-weight: bold;
}

.note-editor.fullwidth .note-textarea {
  height: calc(100% - 64px);
}

.note-toolbar {
  height: 64px;
  flex-shrink: 0;
}

.note-spinner {
  font-size: 32px;
  opacity: 0.8;
  color: $col-button;
}

#btn-save {
  margin-left: 20px;
}

#btn-save.disabled {
  opacity: 0.2;
}

.note-editor .upmenu {
  padding-bottom: 8px;
  background: #fff;
  border-radius: 50% 50% 0px 0px;
}

.editor-menu {
  right: 65px;
}

.standalone .editor-menu {
  right: 10px;
}

.btn-search-mobile {
  display: none;
  margin-left: 8px;
}

@media screen and (max-width: $breakpoint-md) {
  .btn-search-mobile {
    display: flex;
  }
}

@media screen and (min-width: $breakpoint-md) {
  .note-editor {
    top: $topbar-height-desktop;
  }
}
</style>
